import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays, Plus, Trash2, Gift, Loader2, X, Bell, Cake, Heart, GraduationCap,
  Baby, Gem, HeartPulse, Sparkles, ChevronDown,
} from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import ProductCard from "@/components/ProductCard";

const ICON = { cake: Cake, heart: Heart, "graduation-cap": GraduationCap, baby: Baby, gem: Gem, "heart-pulse": HeartPulse, sparkles: Sparkles, calendar: CalendarDays };
const REMINDERS = [30, 14, 7, 1];
const emptyForm = () => ({ title: "", occ_type: "birthday", dateStr: "", recipient_name: "", reminder_days: [30, 14, 7, 1] });

export default function Occasions() {
  const { lang, user, formatPrice } = useApp();
  const ar = lang === "ar";
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [sugg, setSugg] = useState({});   // {oid: {open, loading, items}}

  const load = async () => {
    setLoading(true);
    try { const { data } = await http.get("/occasions"); setItems(data || []); } catch (e) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); http.get("/occasion-types").then(({ data }) => setTypes(data || [])).catch(() => {}); }, []);

  const save = async () => {
    if (!form.dateStr) return;
    const d = new Date(form.dateStr);
    setSaving(true);
    try {
      await http.post("/occasions", {
        title: form.title, occ_type: form.occ_type, recipient_name: form.recipient_name,
        month: d.getMonth() + 1, day: d.getDate(), year: d.getFullYear(), reminder_days: form.reminder_days,
      });
      setShowForm(false); setForm(emptyForm()); load();
    } catch (e) {} finally { setSaving(false); }
  };

  const del = async (id) => { try { await http.delete(`/occasions/${id}`); setItems((x) => x.filter((o) => o.id !== id)); } catch (e) {} };

  const toggleSugg = async (oid) => {
    setSugg((s) => ({ ...s, [oid]: { ...(s[oid] || {}), open: !(s[oid]?.open) } }));
    if (!sugg[oid]?.items) {
      setSugg((s) => ({ ...s, [oid]: { ...(s[oid] || {}), open: true, loading: true } }));
      try { const { data } = await http.get(`/occasions/${oid}/gift-suggestions`); setSugg((s) => ({ ...s, [oid]: { open: true, loading: false, items: data.items || [] } })); }
      catch (e) { setSugg((s) => ({ ...s, [oid]: { open: true, loading: false, items: [] } })); }
    }
  };

  const badge = (d) => d <= 3 ? "bg-rose-100 text-rose-700" : d <= 14 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(ar ? "ar" : "en", { day: "numeric", month: "long" });

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center space-y-3">
      <CalendarDays size={40} className="mx-auto opacity-40" />
      <div className="opacity-70">{ar ? "سجّل الدخول لإدارة مناسباتك" : "Log in to manage your occasions"}</div>
      <Link to="/login" className="btn-primary inline-flex" data-testid="occ-login">{ar ? "تسجيل الدخول" : "Log in"}</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 pb-24" data-testid="occasions-page">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className={`${ar ? "font-display-ar" : "font-display-en"} text-2xl sm:text-3xl flex items-center gap-2`}>
            <CalendarDays size={24} style={{ color: "var(--primary)" }} /> {ar ? "مناسباتي" : "My occasions"}
          </h1>
          <p className="text-sm opacity-60 mt-0.5">{ar ? "لا تنسَ مناسبة أبداً — نذكّرك ونقترح الهدية المثالية" : "Never miss a date — we remind you & suggest the perfect gift"}</p>
        </div>
        <button onClick={() => { setForm(emptyForm()); setShowForm(true); }} className="btn-primary shrink-0" data-testid="occ-add-btn"><Plus size={16} /> {ar ? "إضافة" : "Add"}</button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin" size={26} style={{ color: "var(--primary)" }} /></div>
      ) : items.length === 0 ? (
        <div className="card-soft p-10 text-center space-y-3" data-testid="occ-empty">
          <CalendarDays size={44} className="mx-auto opacity-30" />
          <div className="opacity-70">{ar ? "لم تُضف أي مناسبة بعد" : "No occasions yet"}</div>
          <button onClick={() => { setForm(emptyForm()); setShowForm(true); }} className="btn-outline inline-flex" data-testid="occ-add-empty"><Plus size={15} /> {ar ? "أضف أول مناسبة" : "Add your first"}</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="occ-list">
          {items.map((o) => {
            const Icon = ICON[o.type_icon] || CalendarDays; const s = sugg[o.id] || {};
            return (
              <div key={o.id} className="card-soft overflow-hidden" data-testid={`occ-item-${o.id}`}>
                <div className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--bg-subtle)", color: "var(--primary)" }}><Icon size={20} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm line-clamp-1">{o.title || (ar ? o.type_name_ar : o.type_name_en)}{o.recipient_name ? ` · ${o.recipient_name}` : ""}</div>
                    <div className="text-xs opacity-60 flex items-center gap-2">
                      <span>{fmtDate(o.next_date)}</span>
                      <span className="flex items-center gap-0.5"><Bell size={11} /> {o.reminder_days?.join("، ")} {ar ? "يوم" : "d"}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${badge(o.days_until)}`} data-testid={`occ-countdown-${o.id}`}>
                    {o.days_until === 0 ? (ar ? "اليوم! 🎉" : "Today! 🎉") : `${ar ? "بعد" : "in"} ${o.days_until} ${ar ? "يوم" : "d"}`}
                  </span>
                </div>
                <div className="px-4 pb-3 flex items-center gap-2">
                  <button onClick={() => toggleSugg(o.id)} className="chip !bg-rose-50 !text-rose-600 !border-rose-200" data-testid={`occ-suggest-${o.id}`}>
                    <Gift size={13} /> {ar ? "اقترح هدايا" : "Gift ideas"} <ChevronDown size={13} className={`transition-transform ${s.open ? "rotate-180" : ""}`} />
                  </button>
                  <div className="flex-1" />
                  <button onClick={() => del(o.id)} className="w-8 h-8 rounded-full grid place-items-center text-rose-500 hover:bg-rose-50" data-testid={`occ-delete-${o.id}`}><Trash2 size={15} /></button>
                </div>
                <AnimatePresence>
                  {s.open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t" style={{ borderColor: "var(--border)" }}>
                      <div className="p-3">
                        {s.loading ? <div className="py-6 grid place-items-center"><Loader2 className="animate-spin" size={20} /></div>
                          : s.items?.length ? (
                            <div className="flex gap-3 overflow-x-auto scroll-x-hide pb-1" data-testid={`occ-suggestions-${o.id}`}>
                              {s.items.map((p) => <div key={p.id} className="w-36 shrink-0"><ProductCard product={p} /></div>)}
                            </div>
                          ) : <div className="py-4 text-center text-sm opacity-60">{ar ? "لا توجد اقتراحات حالياً" : "No suggestions yet"}</div>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/50 grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={() => setShowForm(false)}>
            <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="occ-form">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-lg">{ar ? "إضافة مناسبة" : "Add occasion"}</h2>
                <button onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>
              <div>
                <label className="text-xs opacity-70 block mb-1.5">{ar ? "نوع المناسبة" : "Type"}</label>
                <div className="grid grid-cols-4 gap-2" data-testid="occ-types">
                  {types.map((t) => { const I = ICON[t.icon] || CalendarDays; const on = form.occ_type === t.key; return (
                    <button key={t.key} onClick={() => setForm((f) => ({ ...f, occ_type: t.key }))} className="rounded-xl p-2 flex flex-col items-center gap-1 border transition-all" style={{ borderColor: on ? "var(--primary)" : "var(--border)", background: on ? "var(--bg-subtle)" : "#fff" }} data-testid={`occ-type-${t.key}`}>
                      <I size={16} style={{ color: "var(--primary)" }} /><span className="text-[10px] text-center leading-tight line-clamp-1">{ar ? t.name_ar : t.name_en}</span>
                    </button>); })}
                </div>
              </div>
              <div>
                <label className="text-xs opacity-70 block mb-1">{ar ? "العنوان (اختياري)" : "Title (optional)"}</label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="input-field" placeholder={ar ? "مثال: عيد ميلاد أمي" : "e.g. Mom's birthday"} data-testid="occ-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs opacity-70 block mb-1">{ar ? "التاريخ" : "Date"}</label>
                  <input type="date" value={form.dateStr} onChange={(e) => setForm((f) => ({ ...f, dateStr: e.target.value }))} className="input-field" data-testid="occ-date" />
                </div>
                <div>
                  <label className="text-xs opacity-70 block mb-1">{ar ? "اسم الشخص (اختياري)" : "Recipient (optional)"}</label>
                  <input value={form.recipient_name} onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))} className="input-field" data-testid="occ-recipient" />
                </div>
              </div>
              <div>
                <label className="text-xs opacity-70 block mb-1.5">{ar ? "ذكّرني قبل (أيام)" : "Remind me before (days)"}</label>
                <div className="flex gap-2">
                  {REMINDERS.map((r) => { const on = form.reminder_days.includes(r); return (
                    <button key={r} onClick={() => setForm((f) => ({ ...f, reminder_days: on ? f.reminder_days.filter((x) => x !== r) : [...f.reminder_days, r] }))} className={`chip flex-1 justify-center ${on ? "active" : ""}`} data-testid={`occ-reminder-${r}`}>{r}</button>); })}
                </div>
              </div>
              <button onClick={save} disabled={saving || !form.dateStr} className="btn-primary w-full justify-center" data-testid="occ-save">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} {ar ? "حفظ المناسبة" : "Save occasion"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
